/**
 * x-file-manager: ファイル選択・プレビュー・file_status 同期
 * 依存: window.showConfirm（resources/js/components/confirm-dialog.js）
 */

import { FileManagerFileStatus } from '../enums/file-manager-file-status';

/** @type {Readonly<Record<'NONE'|'NEW'|'UPDATE'|'DELETE', string>>} */
const FM_STATUS = {
    NONE: FileManagerFileStatus.None,
    NEW: FileManagerFileStatus.NewUpload,
    UPDATE: FileManagerFileStatus.Replace,
    DELETE: FileManagerFileStatus.Delete,
};

/**
 * @param {HTMLElement} root
 * @param {string} field
 * @returns {HTMLInputElement|null}
 */
function fmField(root, field) {
    return root.querySelector(`[data-fm-field="${field}"]`);
}

/**
 * @param {HTMLElement} root
 * @param {string} field
 * @param {string} value
 */
function setFmField(root, field, value) {
    const el = fmField(root, field);
    if (el) {
        el.value = value;
    }
}

/**
 * @param {HTMLElement} root
 * @param {string} field
 * @returns {string}
 */
function getFmField(root, field) {
    const el = fmField(root, field);
    return el ? String(el.value) : '';
}

/**
 * @param {HTMLElement} root
 * @returns {boolean}
 */
function hasPersistedFile(root) {
    return getFmField(root, 'id') !== '' || getFmField(root, 'path') !== '';
}

/**
 * @param {HTMLElement} root
 * @returns {boolean}
 */
function hadPersistedFile(root) {
    return hasPersistedFile(root) || root.dataset.hasExisting === '1';
}

/**
 * @param {HTMLElement} root
 */
function clearBlobPreview(root) {
    const prev = root._fmBlobUrl;
    if (prev) {
        URL.revokeObjectURL(prev);
        root._fmBlobUrl = '';
    }
}

/**
 * @param {HTMLElement} root
 * @param {string} url
 */
function setPreviewImageSrc(root, url) {
    const img = root.querySelector('[data-fm-preview-img]');
    const placeholder = root.querySelector('[data-fm-preview-placeholder]');
    if (!img) {
        return;
    }
    img.src = url;
    img.classList.remove('d-none');
    if (placeholder) {
        placeholder.classList.add('d-none');
    }
}

/**
 * @param {HTMLElement} root
 */
function hidePreviewImage(root) {
    const img = root.querySelector('[data-fm-preview-img]');
    const placeholder = root.querySelector('[data-fm-preview-placeholder]');
    if (img) {
        img.removeAttribute('src');
        img.classList.add('d-none');
    }
    if (placeholder) {
        placeholder.classList.remove('d-none');
    }
}

/**
 * @param {HTMLElement} root
 */
function restoreEmptyPreview(root) {
    const emptyPreviewUrl = (root.dataset.emptyPreviewUrl || '').trim();
    if (emptyPreviewUrl === '') {
        hidePreviewImage(root);
        return;
    }
    setPreviewImageSrc(root, emptyPreviewUrl);
}

/**
 * @param {HTMLElement} root
 * @param {File} file
 */
function updateFilenameDisplay(root, file) {
    const wrap = root.querySelector('[data-fm-filename-wrap]');
    const text = root.querySelector('[data-fm-filename-text]');
    if (!text) {
        return;
    }
    const emptyLabel = root.dataset.noFileLabel || '';
    const name = file ? file.name : '';
    text.textContent = name || emptyLabel;
    if (wrap) {
        wrap.classList.toggle('text-muted', name === '');
    }
}

/**
 * @param {HTMLElement} root
 * @param {boolean} showUpload
 * @param {boolean} showDelete
 * @param {boolean} showDownload
 */
function toggleActionButtons(root, showUpload, showDelete, showDownload) {
    const sel = root.querySelector('[data-fm-select]');
    const del = root.querySelector('[data-fm-delete]');
    const dl = root.querySelector('[data-fm-download]');
    if (sel) {
        sel.classList.toggle('d-none', !showUpload);
    }
    if (del) {
        del.classList.toggle('d-none', !showDelete);
    }
    if (dl) {
        dl.classList.toggle('d-none', !showDownload);
    }
}

/**
 * @param {HTMLElement} root
 */
export function initFileManager(root) {
    if (root.getAttribute('data-file-manager-initialized') === '1') {
        return;
    }
    const mode = root.dataset.mode || 'file';
    const fileInput = root.querySelector('[data-fm-file-input]');
    const selectBtn = root.querySelector('[data-fm-select]');
    const deleteBtn = root.querySelector('[data-fm-delete]');
    const downloadLink = root.querySelector('[data-fm-download]');
    const confirmMsg = root.dataset.confirmDelete || '';

    if (!fileInput) {
        return;
    }

    /**
     * 既存ファイルのダウンロードを表示するか（サーバにファイルがあり、削除未確定）
     * @param {string} status
     */
    function shouldShowDownload(status) {
        if (!downloadLink) {
            return false;
        }
        if (status === FM_STATUS.DELETE) {
            return false;
        }
        const href = downloadLink.getAttribute('href') || '';
        const fromData = (root.dataset.downloadUrl || '').trim();
        const effectiveHref =
            href !== '' && href !== '#' ? href : fromData;
        if (effectiveHref === '' || effectiveHref === '#') {
            return false;
        }
        if (!hadPersistedFile(root)) {
            return false;
        }
        if (fileInput.files && fileInput.files.length > 0) {
            return false;
        }
        return true;
    }

    function refreshDownloadHref() {
        if (!downloadLink) {
            return;
        }
        const fromData = root.dataset.downloadUrl || '';
        if (fromData) {
            downloadLink.setAttribute('href', fromData);
        }
    }

    function applyUiForStatus(status) {
        refreshDownloadHref();
        const st = String(status);
        const hasServer = hadPersistedFile(root);
        const hasLocalFile = fileInput.files && fileInput.files.length > 0;

        if (st === FM_STATUS.DELETE) {
            toggleActionButtons(root, true, false, false);
            return;
        }

        if (hasLocalFile) {
            toggleActionButtons(root, true, true, false);
            return;
        }

        if (hasServer) {
            toggleActionButtons(root, true, true, shouldShowDownload(st));
            return;
        }

        toggleActionButtons(root, true, false, false);
    }

    if (selectBtn) {
        selectBtn.addEventListener('click', () => {
            fileInput.click();
        });
    }

    if (downloadLink) {
        downloadLink.addEventListener('click', (e) => {
            refreshDownloadHref();
            const href = downloadLink.getAttribute('href') || '';
            const fromData = (root.dataset.downloadUrl || '').trim();
            const effective =
                href !== '' && href !== '#' ? href : fromData;
            if (effective === '' || effective === '#') {
                e.preventDefault();
            } else if (href === '' || href === '#') {
                downloadLink.setAttribute('href', effective);
            }
        });
    }

    fileInput.addEventListener('change', () => {
        const files = fileInput.files;
        if (!files || files.length === 0) {
            return;
        }
        const file = files[0];
        const hasServerFile = hadPersistedFile(root);

        setFmField(root, 'original_name', file.name);
        setFmField(root, 'path', '');

        if (hasServerFile) {
            setFmField(root, 'file_status', FM_STATUS.UPDATE);
        } else {
            setFmField(root, 'file_status', FM_STATUS.NEW);
        }

        const previewWrap = root.querySelector('[data-fm-preview-wrap]');
        const filenameWrap = root.querySelector('[data-fm-filename-wrap]');

        if (mode === 'image') {
            clearBlobPreview(root);
            const url = URL.createObjectURL(file);
            root._fmBlobUrl = url;
            setPreviewImageSrc(root, url);
        } else if (mode === 'mixed') {
            clearBlobPreview(root);
            if (file.type.startsWith('image/')) {
                if (previewWrap) {
                    previewWrap.classList.remove('d-none');
                }
                if (filenameWrap) {
                    filenameWrap.classList.add('d-none');
                }
                const url = URL.createObjectURL(file);
                root._fmBlobUrl = url;
                setPreviewImageSrc(root, url);
            } else {
                hidePreviewImage(root);
                if (previewWrap) {
                    previewWrap.classList.add('d-none');
                }
                if (filenameWrap) {
                    filenameWrap.classList.remove('d-none');
                }
                updateFilenameDisplay(root, file);
            }
        } else {
            updateFilenameDisplay(root, file);
        }

        applyUiForStatus(getFmField(root, 'file_status'));
    });

    if (deleteBtn) {
        deleteBtn.addEventListener('click', async () => {
            let ok = true;
            if (typeof window.showConfirm === 'function' && confirmMsg) {
                ok = await window.showConfirm(confirmMsg);
            }
            if (!ok) {
                return;
            }

            const hadServerFile = hadPersistedFile(root);

            clearBlobPreview(root);
            fileInput.value = '';

            const previewWrapAfterDelete = root.querySelector('[data-fm-preview-wrap]');
            const filenameWrapAfterDelete = root.querySelector('[data-fm-filename-wrap]');

            if (hadServerFile) {
                setFmField(root, 'file_status', FM_STATUS.DELETE);
                setFmField(root, 'path', '');
                if (mode === 'image') {
                    restoreEmptyPreview(root);
                } else if (mode === 'mixed') {
                    hidePreviewImage(root);
                    if (previewWrapAfterDelete) {
                        previewWrapAfterDelete.classList.add('d-none');
                    }
                    if (filenameWrapAfterDelete) {
                        filenameWrapAfterDelete.classList.add('d-none');
                    }
                    updateFilenameDisplay(root, null);
                } else {
                    updateFilenameDisplay(root, null);
                }
            } else {
                setFmField(root, 'file_status', FM_STATUS.NONE);
                setFmField(root, 'original_name', '');
                if (mode === 'image') {
                    restoreEmptyPreview(root);
                } else if (mode === 'mixed') {
                    hidePreviewImage(root);
                    if (previewWrapAfterDelete) {
                        previewWrapAfterDelete.classList.add('d-none');
                    }
                    if (filenameWrapAfterDelete) {
                        filenameWrapAfterDelete.classList.add('d-none');
                    }
                    updateFilenameDisplay(root, null);
                } else {
                    updateFilenameDisplay(root, null);
                }
            }

            applyUiForStatus(getFmField(root, 'file_status'));
        });
    }

    applyUiForStatus(getFmField(root, 'file_status'));

    root.setAttribute('data-file-manager-initialized', '1');
}

/**
 * @param {ParentNode} [root=document]
 */
export function initFileManagers(root = document) {
    root.querySelectorAll('[data-file-manager]').forEach((el) => {
        if (el instanceof HTMLElement) {
            initFileManager(el);
        }
    });
}

/**
 * ES modules run deferred; if this bundle loads after DOMContentLoaded, a bare
 * DOMContentLoaded listener would never run and file_status would stay 0 on submit.
 */
function bootFileManagers() {
    initFileManagers();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootFileManagers);
} else {
    bootFileManagers();
}
